import { useState } from "react";
import styles from "./attendanceCard.module.scss";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faRightLeft } from "@fortawesome/free-solid-svg-icons";
import useDeleteSchedule from "hooks/useDeleteSchedule";
import { convertSessionToPractice } from "api/admin";
import { PREMIUM_FIXED_PRICE, formatPriceKRW } from "constants/lessonRequest";
import { useSnackbar } from "contexts/SnackbarContext";
import Badge from "../common/Badge";

const CONVERT_SESSION_ERROR_MESSAGES = {
    SESSION_NOT_FOUND: "강습을 찾을 수 없습니다.",
    FORBIDDEN: "본인 강습만 전환 가능합니다.",
    SESSION_NOT_CONVERTIBLE: "OPEN 상태 강습만 전환 가능합니다.",
    SESSION_HAS_RESERVATIONS: "예약자가 있는 강습은 전환할 수 없습니다.",
    CONVERSION_TOO_LATE: "강습 시작 72시간 전까지만 전환 가능합니다.",
    PRICE_REQUIRED: "유효한 가격을 입력해주세요.",
    INVALID_BOOLEAN: "allow_ticket 값이 올바르지 않습니다.",
    PAIRED_PRACTICE_SLOT_CONFLICT: "같은 시간대에 다른 장소의 연습반이 이미 존재합니다.",
    ALREADY_PAID: "이미 유료 연습반입니다.",
};

const CONVERT_CONFIRM_MESSAGE =
    "이 강습을 프리미엄 트레이닝(유료 연습반)으로 전환합니다.\n\n" +
    `• 가격: ${formatPriceKRW(PREMIUM_FIXED_PRICE)} (고정)\n` +
    "• 횟수권 사용 불가\n" +
    "• 전환 시 되돌릴 수 없습니다.\n\n" +
    "진행하시겠습니까?";

// student_count가 없거나(null/undefined) 숫자가 아니면 0명으로 본다.
const toStudentCount = (value) => (Number.isFinite(value) ? value : 0);

const AttendanceCard = ({ sch, isCompleted = false, onDeleted, onConverted }) => {
    const { handleDelete: deleteSchedule, isDeleting } = useDeleteSchedule();
    const { showSuccess, showError } = useSnackbar();
    const [isConverting, setIsConverting] = useState(false);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        const type = sch.type.includes('session') ? 'lesson': 'practice'
        deleteSchedule(sch.id, type, {
            onSuccess: () => onDeleted?.(sch.id),
        });
    };

    const canConvert =
        !isCompleted &&
        sch.session !== null &&
        sch.practice === null &&
        (sch.session?.student_count ?? 0) === 0;

    const handleConvertClick = async (e) => {
        e.stopPropagation();
        if (isConverting) return;
        const sessionId = sch.session?.id ?? sch.id;
        if (!sessionId) {
            showError("세션 정보가 없습니다.");
            return;
        }
        if (!window.confirm(CONVERT_CONFIRM_MESSAGE)) return;
        setIsConverting(true);
        try {
            const res = await convertSessionToPractice(sessionId);
            if (res?.status >= 200 && res?.status < 300) {
                showSuccess("프리미엄 트레이닝으로 전환되었습니다.");
                onConverted?.(sch.id);
                return;
            }
            const errBody = res?.response?.data || {};
            const errMsg =
                CONVERT_SESSION_ERROR_MESSAGES[errBody.code] ||
                errBody.error ||
                errBody.message ||
                "전환에 실패했습니다.";
            showError(errMsg);
        } catch (error) {
            showError("전환 처리 중 오류가 발생했습니다.");
        } finally {
            setIsConverting(false);
        }
    };

    const today = dayjs().startOf("day");
    const classDate = dayjs(sch.date);
    const isToday = classDate.isSame(today, "day");

    const timeRange = `${(sch.start_time ?? "").slice(0, -3)} - ${(sch.end_time ?? "").slice(0, -3)}`;
    const formattedDate = classDate.format("M월 D일 (ddd)");

    // 뱃지 분류: 프리미엄 / 강습 / 연습 (기존 로직)
    const isPremiumPractice =
        sch.practice?.name?.includes("프리미엄 트레이닝") ||
        sch.practice?.practice_type === "REGULAR_TRAINING";

    const handleCardClick = () => {
        // 새 창으로 열면 location.state를 전달할 수 없어 type을 쿼리스트링으로 넘긴다.
        const detailType = sch.type?.includes("session") ? "session" : "practice";
        window.open(`/attendance/${sch.id}?type=${detailType}`, "_blank");
    };

    // 예약 인원은 정원(capacity)이 아니라 실제 신청한 수강생 수(student_count)를 본다.
    // "예약(수업/연습반)" 라벨에 맞춰 수업 예약자 / 연습반 예약자를 각각 표기하며,
    // 값이 없거나 숫자가 아니면 0명으로 표기한다.
    const sessionCount = toStudentCount(sch.session?.student_count);
    const practiceCount = toStudentCount(sch.practice?.student_count);

    return (
        <div
            className={`${styles["card-wrap"]} ${
                isCompleted ? styles["completed"] : ""
            } ${isToday ? styles['today']:''}`}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            aria-label={`${sch.type === 'practice' ? sch.practice?.name : sch.session?.name} 출석 관리. 시간: ${timeRange}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCardClick();
                }
            }}
        >
            <div className={styles["card-header"]}>
                <div className={styles["left-section"]}>
                    {sch.session !== null && (
                      <Badge text="강습" type="soft" color="purple"/>
                    )}
                    {sch.practice !== null && (
                      <Badge text={isPremiumPractice ? "프리미엄" : "연습"} type="soft" color={isPremiumPractice ? "green" : "orange"}/>
                    )}
          
                </div>
                {!isCompleted && (
                    <div className={styles["action-btns"]}>
                        {canConvert && (
                            <button
                                type="button"
                                onClick={handleConvertClick}
                                disabled={isConverting}
                                title="프리미엄 트레이닝으로 전환"
                            >
                                <FontAwesomeIcon icon={faRightLeft} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            title="스케쥴 취소"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                )}
            </div>

            <div className={styles["card-body"]}>
              <div>
              <h3 className={styles["title"]}>
                 
                    {sch.type === "practice"
                        ? sch.practice?.name
                        : sch.session?.name}
                </h3>
                <div className={styles["date-info"]}>
                  <span> {sch.location_name}</span>
                  <span>・</span>
                  <span>{formattedDate}</span>
                  <span>・</span>
                  <span>{timeRange}</span>
                </div>
              </div>
              <div className={`${styles["reservation-badge"]}`}>
                  <p>예약(수업/연습반)</p>
                  <p>{sessionCount}명 / {practiceCount}명</p>
              </div>
            </div>

            
        </div>
    );
};

export default AttendanceCard;
